import React, { createContext, ReactNode, useContext, useState } from 'react'

/**
 * Theme mode type definition
 */
export type ThemeMode = 'light' | 'dark' | 'grayscale' | 'obxops'

/**
 * Theme context value interface
 */
interface ThemeContextValue {
  theme: ThemeMode
  toggleTheme: () => void
  setTheme: (_theme: ThemeMode) => void
}

/**
 * Theme provider props
 */
interface ThemeProviderContextProps {
  children: ReactNode
}

/**
 * Create theme context with default values
 */
export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

/**
 * Custom hook to use theme context
 * Throws error if used outside of ThemeProviderContext
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProviderContext')
  }
  return context
}

/**
 * Theme provider component
 * Manages theme state and persists to localStorage
 */
export const ThemeProviderContext: React.FC<ThemeProviderContextProps> = ({ children }) => {
  // Initialize theme state from localStorage or default to 'dark'
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const storedTheme = localStorage.getItem('theme') as ThemeMode | null
    return storedTheme || 'dark'
  })

  // Toggle theme: light -> dark -> grayscale -> light
  const toggleTheme = (): void => {
    setThemeState(prevTheme => {
      const newTheme: ThemeMode =
        prevTheme === 'light'
          ? 'dark'
          : prevTheme === 'dark'
            ? 'grayscale'
            : prevTheme === 'grayscale'
              ? 'obxops'
              : 'light'

      localStorage.setItem('theme', newTheme)
      return newTheme
    })
  }

  // Directly set theme (useful for user preferences, testing, etc.)
  const setTheme = (newTheme: ThemeMode): void => {
    setThemeState(newTheme)
    localStorage.setItem('theme', newTheme)
  }

  const value: ThemeContextValue = {
    theme,
    toggleTheme,
    setTheme,
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
